import React, { useEffect, useRef } from 'react';
import { ChatMessage as ChatMessageType } from '@/types/chat';
import { MessageBubble } from './MessageBubble';
import { DeletedMessageBubble } from './DeletedMessageBubble';
import { AttachmentBubble } from './AttachmentBubble';
import { ReplyMessageBubble } from './ReplyMessageBubble';
import { AttachmentReplyBubble } from './AttachmentReplyBubble';

interface ChatMessageListProps {
  messages: ChatMessageType[];
  onReply: (message: ChatMessageType) => void;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  onReply,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Function to find the message being replied to
  const findReplyMessage = (replyId?: string): ChatMessageType | undefined => {
    if (!replyId) return undefined;
    return messages.find((msg) => msg.id === replyId);
  };

  if (!messages.length) {
    return (
      <div className='flex-1 flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900'>
        <div className='text-center p-6 rounded-lg bg-white dark:bg-gray-800 shadow-sm'>
          <p className='text-xl font-medium mb-1'>No messages yet</p>
          <p className='text-muted-foreground'>
            Start the conversation by sending a message!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50 dark:bg-gray-900'>
      {messages.map((message) => {
        const replyToMessage = findReplyMessage(message.reply_to);

        // System message for when someone joins
        if (message.message_type === 'system') {
          return (
            <div key={message.id} className='text-center my-4'>
              <div className='inline-block bg-muted px-3 py-1 rounded-full text-xs'>
                {message.content}
              </div>
            </div>
          );
        }

        // Deleted message
        if (message.is_deleted) {
          return <DeletedMessageBubble key={message.id} message={message} />;
        }

        // Message with attachment and reply
        if (
          (message.message_type === 'image' ||
            message.message_type === 'video') &&
          message.reply_to
        ) {
          return (
            <AttachmentReplyBubble
              key={message.id}
              message={message}
              replyToMessage={replyToMessage}
              onReply={onReply}
            />
          );
        }

        // Message with attachment
        if (
          message.message_type === 'image' ||
          message.message_type === 'video'
        ) {
          return (
            <AttachmentBubble
              key={message.id}
              message={message}
              onReply={onReply}
            />
          );
        }

        // Reply message
        if (message.reply_to) {
          return (
            <ReplyMessageBubble
              key={message.id}
              message={message}
              replyToMessage={replyToMessage}
              onReply={onReply}
            />
          );
        }

        // Regular message
        return (
          <MessageBubble key={message.id} message={message} onReply={onReply} />
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};
